export type PermissionLevel = 100 | 300 | 600 | 900;

export const PERMISSION_LEVEL_ADMIN = 900;
export const PERMISSION_LEVEL_EDIT = 600;
export const PERMISSION_LEVEL_VIEW = 300;
export const PERMISSION_LEVEL_EXTERNAL = 100;

export const PERMISSION_LEVELS = [
  PERMISSION_LEVEL_EXTERNAL,
  PERMISSION_LEVEL_VIEW,
  PERMISSION_LEVEL_EDIT,
  PERMISSION_LEVEL_ADMIN,
] as const;

/**
 * Display label for each permission tier. Used by autogenerated folder-permission
 * groups when their canonical name is constructed/refreshed (Folders.ts:166-218,
 * 815-838). The DB also stores these labels in `permission_types.label` and bakes
 * them into `groups.name`. Any rename here REQUIRES a hickory migration that
 * updates both permission_types.label and existing groups.name suffixes —
 * otherwise the dropdown labels and the persisted group names will drift.
 */
export const PERMISSION_LEVEL_TO_LABEL: Record<number, string> = {
  [PERMISSION_LEVEL_ADMIN]: "Admin",
  [PERMISSION_LEVEL_EDIT]: "Asset Editor",
  [PERMISSION_LEVEL_VIEW]: "Contributor",
  [PERMISSION_LEVEL_EXTERNAL]: "Uploader",
};

export const ORGANIZATION_ROLES = ["admin", "member", "fybr_admin"] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

export const isOrganizationRole = (role: string): role is OrganizationRole =>
  (ORGANIZATION_ROLES as readonly string[]).includes(role);

/** Roles that implicitly hold PERMISSION_LEVEL_ADMIN (900) on every folder in their own organization. */
export const ORG_ADMIN_ROLES = ["admin", "fybr_admin"] as const;
export type OrgAdminRole = (typeof ORG_ADMIN_ROLES)[number];

export const isOrgAdminRole = (role: string): role is OrgAdminRole =>
  (ORG_ADMIN_ROLES as readonly string[]).includes(role);

/** Quoted list for SQL IN clauses: `'admin', 'fybr_admin'`. Trusted constants, safe to interpolate. */
export const ORG_ADMIN_ROLES_SQL = ORG_ADMIN_ROLES.map((r) => `'${r}'`).join(", ");

/**
 * "This account can actually sign in." A user row exists from the moment they are invited;
 * password_digest stays empty until they accept (see activateUser, accessors/User.ts).
 * Alias-bound: callers must expose the users table as `users`.
 */
export const ACTIVATED_USER_SQL =
  "users.password_digest IS NOT NULL AND users.password_digest <> ''";

/**
 * Display label for each organization role in server-rendered output (emails).
 * Spells out "Organization Admin" deliberately: folder permission level 900 is also
 * labelled "Admin" (PERMISSION_LEVEL_TO_LABEL) and both can appear in one email.
 * pimento/src/components/Admin/Organization/users.tsx still hardcodes its own shorter
 * dropdown labels (and spells "Fybr Admin" with title-case "Fybr", not "FYBR");
 * unify when that page is next touched.
 */
export const ORGANIZATION_ROLE_LABELS: Record<OrganizationRole, string> = {
  admin: "Organization Admin",
  fybr_admin: "FYBR Admin",
  member: "Member",
};
